import React, { useState } from 'react';
import Grid from '@mui/material/Grid2';

import { TransformUpgradeFeature } from "@dndModels";
import { CREATURE_TYPES, SIZE_OPTIONS } from "@dndModels/Constants";
import { CreatureType, SizeType } from '@dndModels/Types';

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CenteredMenu from '@components/input/menus/CenteredMenu';

import CreatureLimitEnhancementInput from './CreatureLimitEnhancementInput';
import FromSourceWithExtraInput from './FromSourceWithExtraInput';
import PairedNumberEnhancementInput from './PairedNumberEnhancementInput';
import TransformStatsEnhancementInput from './TransformStatsEnhancementInput';
import TransformAttributesEnhancementInput from './TransformAttributesEnhancementInput';
import ActionLimitationEnhancementInput from './ActionLimitationEnhancementInput';
import FeatureBaseListEnhancementInput from './FeatureBaseListEnhancementInput';
import GiftIdsEnhancementInput from './GiftIdsEnhancementInput';
import EnhancementModeBox from './EnhancementModeBox';

interface Props {
  obj: TransformUpgradeFeature;
  onChange: (changed: TransformUpgradeFeature) => void;
  disabled: boolean;
}

export default function TransformUpgradeFeatureInput({
  obj,
  onChange,
  disabled
}: Props) {
  const [mode, setMode] = useState("Home");
  const [ping, setPing] = useState(false);

  // A TransformUpgradeFeature can apply to several different Transforms, some of
  // which use a creature and some of which don't - unlike TransformOptionInput there's
  // no single use_creature flag to gate the "from creature" fields behind, so they're
  // always shown here. Happy to add a real toggle if you'd rather preview against a
  // specific Transform's use_creature setting instead.
  const use_creature = true;

  const bump = () => {
    onChange(obj);
    setPing(!ping);
  }

  const renderHome = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12}>
          <StringBox
            name="Transform Tag"
            value={obj.transform_tag}
            message={obj.transform_tag.length > 0 ? "" : "Tag Invalid"}
            disabled={disabled}
            onBlur={(changed: string) => {
              obj.transform_tag = changed;
              bump();
            }}
          />
        </Grid>

        <Grid size={8}>
          <SelectStringBox
            name="Creature Type"
            options={CREATURE_TYPES}
            extra_options={["Leave"]}
            value={obj.creature_type === undefined ? "Leave" : obj.creature_type}
            auto_sort
            disabled={disabled}
            onChange={(changed: CreatureType | "Leave") => {
              if (changed === "Leave") {
                delete obj.creature_type;
              } else {
                obj.creature_type = changed;
              }
              bump();
            }}
          />
        </Grid>

        <Grid size={12}>
          <SelectStringBox
            name="Swarm"
            options={["Yes","No","Leave"]}
            value={obj.swarm === undefined ? "Leave" : (obj.swarm ? "Yes" : "No")}
            disabled={disabled}
            onChange={(changed: "Yes" | "No" | "Leave") => {
              switch (changed) {
                case "Leave": delete obj.swarm; break;
                default: obj.swarm = changed === "Yes"; break;
              }
              bump();
            }}
          />
        </Grid>

        <Grid size={12}>
          <PairedNumberEnhancementInput
            name="Size"
            value={obj.size}
            use_creature={use_creature}
            disabled={disabled}
            onChange={bump}
          />
        </Grid>

        <Grid size={12}>
          <CreatureLimitEnhancementInput
            value={obj.creature_limit}
            disabled={disabled}
            onChange={bump}
          />
        </Grid>

        <Grid size={12}>
          <PairedNumberEnhancementInput
            name="Max Hit Points"
            value={obj.max_hp}
            use_creature={use_creature}
            disabled={disabled}
            onChange={bump}
          />
        </Grid>

        <Grid size={12}>
          <PairedNumberEnhancementInput
            name="Temp Hit Points"
            value={obj.temp_hit_points}
            use_creature={use_creature}
            disabled={disabled}
            onChange={bump}
          />
        </Grid>

        <Grid size={12}>
          <PairedNumberEnhancementInput
            name="Challenge Rating"
            value={obj.cr}
            disabled={disabled}
            use_creature={use_creature}
            onChange={bump}
          />
        </Grid>

        <Grid size={12}>
          <PairedNumberEnhancementInput
            name="Proficiency Bonus"
            value={obj.pb}
            use_creature={use_creature}
            disabled={disabled}
            onChange={bump}
          />
        </Grid>

        <Grid size={12}>
          <PairedNumberEnhancementInput
            name="Initiative Modifier"
            value={obj.init}
            use_creature={use_creature}
            disabled={disabled}
            onChange={bump}
          />
        </Grid>

        <Grid size={12}>
          <PairedNumberEnhancementInput
            name="Armor Class"
            value={obj.ac}
            use_creature={use_creature}
            disabled={disabled}
            onChange={bump}
          />
        </Grid>

        <Grid size={12}>
          <GiftIdsEnhancementInput
            value={obj.built_in_gift_ids}
            disabled={disabled}
            onChange={bump}
          />
        </Grid>
      </Grid>
    );
  }

  const renderActionLimits = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12}>
          <ActionLimitationEnhancementInput
            header="From Target"
            value={obj.char_action_limits}
            disabled={disabled}
            onChange={bump}
          />
        </Grid>
      </Grid>
    );
  }

  const render_tab = () => {
    if (mode === "Attributes") {
      return (
        <TransformAttributesEnhancementInput
          attributes={obj.attributes}
          use_creature={use_creature}
          disabled={disabled}
          onChange={bump}
        />
      );
    } else if (mode === "Ability Scores") {
      return (
        <TransformStatsEnhancementInput
          ability_scores={obj.ability_scores}
          use_creature={use_creature}
          disabled={disabled}
          onChange={bump}
        />
      );
    } else if (mode === "Traits") {
      return (
        <FeatureBaseListEnhancementInput
          feature_source={obj}
          creature_fb_type="Traits"
          value={obj.fbs}
          disabled={disabled}
          onChange={bump}
        />
      );
    } else if (mode === "Actions") {
      return (
        <FeatureBaseListEnhancementInput
          feature_source={obj}
          creature_fb_type="Actions"
          value={obj.fbs}
          disabled={disabled}
          onChange={bump}
        />
      );
    } else if (mode === "Legendary Actions") {
      return (
        <FeatureBaseListEnhancementInput
          feature_source={obj}
          creature_fb_type="Legendary Actions"
          value={obj.fbs}
          disabled={disabled}
          onChange={bump}
        />
      );
    } else if (mode === "Inherited Actions/Traits") {
      return renderActionLimits();
    } else {
      return renderHome();
    }
  }

  return (
    <Grid container spacing={0} direction="row">
      <Grid size={12}>
        <CenteredMenu
          options={["Home","Attributes","Ability Scores","Actions","Traits","Legendary Actions","Inherited Actions/Traits"]}
          selected={mode}
          onSelect={setMode}
        />
      </Grid>
      <Grid size={12}
        style={{ 
          overflowY: "scroll", 
          overflowX: "hidden" 
        }}>
        { render_tab() }
      </Grid>
    </Grid>
  );
}
